<template lang="pug">
  div.iq-tools-view-map-container
    div.iq-tools-view-map-inner
      vue-flow(ref="vueFlow"
            :nodes="nodes"
            :edges="edges"
            @nodes-initialized="layoutGraph"
            @node-click="handleNodeClick"
            :min-zoom="DIAGRAM_ZOOM_MIN"
            :max-zoom="DIAGRAM_ZOOM_MAX"
            :node-types="nodeTypes")
        template(#edge-custom="customEdgeProps")
          custom-edge(v-bind="customEdgeProps")
        controls(position="top-right"
                @interaction-change="onInteractionChange")
</template>

<script>
import { cloneDeep, isNil } from 'lodash';
import { nextTick, markRaw} from 'vue';
import { VueFlow } from '@vue-flow/core';
import { Controls } from '@vue-flow/controls';
import WorkflowNode from './Nodes/WorkflowNode.vue';
import DialogNode from './Nodes/DialogNode.vue';
import ResponseNode from './Nodes/ResponseNode.vue';
import CommandNode from './Nodes/CommandNode.vue';
import CustomEdge from './Edges/CustomEdge.vue';
import { IQ_TOOL_DIAGRAM_EDITABLE_NODE_TYPES, IQ_TOOL_DIAGRAM_EXPANDABLE_NODE_TYPES } from '@/constants/iqtool';

import dagre from '@dagrejs/dagre';
import {
  breadthFirstSearch,
  findLinearChain,
  DIAGRAM_ZOOM_DEFAULT,
  DIAGRAM_ZOOM_MIN,
  DIAGRAM_ZOOM_MAX,
  NODE_WIDTH,
  DEFAULT_NODE_HEIGHT_FOR_LAYOUT,
} from './DiagramHelper';

export default {
  name: 'IqToolsDiagram',
  components: {
    VueFlow,
    Controls,
    CustomEdge,
  },
  emits: ['edit', 'expand'],
  props: {
    initialNodes: {
      type: Array,
      default: () => [],
    },
    initialEdges: {
      type: Array,
      default: () => [],
    },
    initialContainers: {
      type: Array,
      default: () => [],
    },
    expandedNodeIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      nodes: [],
      edges: [],
      isLocked: false,
      subGraphCounter: 0,
      subgraphRows: [],
      NODE_GAP: 50,
      RANK_GAP: 80,
      CONTAINER_PADDING: 20,
      CONTAINER_CHILD_GAP: 50,
      // Graph indexes for performance
      outgoingBySource: {},
      incomingByTarget: {},
      nodeIndexById: {},
      edgeIndexById: {},
      nodeTypes: {
        workflow: markRaw(WorkflowNode),
        dialog: markRaw(DialogNode),
        response: markRaw(ResponseNode),
        command: markRaw(CommandNode),
      },
      DIAGRAM_ZOOM_DEFAULT,
      DIAGRAM_ZOOM_MIN,
      DIAGRAM_ZOOM_MAX,
    };
  },
  watch: {
    initialNodes: {
      handler() {
        this.processInitialData();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Update node data in-place via Vue Flow's API. Also syncs the local
     * this.nodes array so that subsequent operations (e.g. show/hide toggle)
     * preserve the updated values.
     */
    updateNodeDataByTypeAndId(nodeType, dataId, updatedFields) {
      const vueFlow = this.$refs.vueFlow;
      if (!vueFlow) return;

      for (const node of this.nodes) {
        if (node.type === nodeType && Number(node.data?.id) === Number(dataId)) {
          // Update Vue Flow's internal reactive state (triggers re-render)
          vueFlow.updateNodeData(node.id, updatedFields);
          // Keep local array in sync so show/hide toggle doesn't revert
          node.data = { ...node.data, ...updatedFields };
        }
      }
    },

    processInitialData() {
      if (!this.initialNodes?.length) return;

      this.nodes = [];
      this.edges = [];
      this.subGraphCounter = 0;
      this.subgraphRows = [];

      const graphData = {
        nodes: cloneDeep(this.initialNodes),
        edges: cloneDeep(this.initialEdges || []),
        containers: cloneDeep(this.initialContainers || []),
      };

      graphData.nodes.forEach((node, index) => {
        node.data = node.data || {};

        if (IQ_TOOL_DIAGRAM_EDITABLE_NODE_TYPES.includes(node.type)) {
          node.data.isEditable = true;
          node.data.onEditNode = (type, id, name, flowNodeId) => {
            this.$emit('edit', { type, id, name, flowNodeId });
          };
        }

        if (IQ_TOOL_DIAGRAM_EXPANDABLE_NODE_TYPES.includes(node.type)) {
          node.data.hideExpand = index === 0 || this.expandedNodeIds.includes(node.id);
          node.data.onExpandNode = (flowNodeId) => {
            this.$emit('expand', { flowNodeId });
          };
        } else {
          node.data.hideExpand = true;
        }
      });

      this.addSubGraph(graphData);
    },

    onInteractionChange(locked) {
      this.isLocked = locked;
    },

    resetDiagram() {
      this.buildGraphIndexes();
      this.resetEdgesVisibility();
      this.recomputeAllHighlights();
    },

    // BFS wrapper: returns ordered list of visited node ids
    bfs(startNodeId, depth = null) {
      return breadthFirstSearch(this.nodes, this.edges, startNodeId, depth);
    },

    // Linear chain wrapper: follows single-child links
    findChain(startNodeId, depth = null) {
      return findLinearChain(this.nodes, this.edges, startNodeId, depth);
    },

    // Build indexes for quick graph operations
    buildGraphIndexes() {
      this.outgoingBySource = {};
      this.incomingByTarget = {};
      this.nodeIndexById = {};
      this.edgeIndexById = {};

      for (let i = 0; i < this.nodes.length; i += 1) {
        const n = this.nodes[i];
        this.nodeIndexById[n.id] = i;
      }

      for (let i = 0; i < this.edges.length; i += 1) {
        const e = this.edges[i];
        this.edgeIndexById[e.id] = i;
        if (!this.outgoingBySource[e.source]) {
          this.outgoingBySource[e.source] = [];
        }
        this.outgoingBySource[e.source].push(e);

        if (!this.incomingByTarget[e.target]) {
          this.incomingByTarget[e.target] = [];
        }
        this.incomingByTarget[e.target].push(e);
      }
    },

    resetEdgesVisibility() {
      for (const e of this.edges) {
        e.hidden = false;
      }
      this.edges = [...this.edges];
    },

    setEdgeHidden(edgeId, hiddenFlag) {
      const idx = this.edgeIndexById[edgeId];
      if (idx === null) {
        return;
      }
      const edge = this.edges[idx];
      edge.hidden = hiddenFlag;
    },

    findEdgeFromTo(sourceId, targetId) {
      const list = this.outgoingBySource[sourceId] || [];
      for (const e of list) {
        if (e.target === targetId) {
          return e;
        }
      }
      return null;
    },

    // Ensures the full linear chain (single-child path) from startId is visible
    expandLinearChainFrom(startId, changedNodeIds = new Set(), changedSourceIds = new Set()) {
      const visited = new Set([startId]);
      let currentId = startId;
      for (;;) {
        const outs = this.outgoingBySource[currentId] || [];
        if (outs.length !== 1) {
          break;
        }
        const edge = outs[0];
        const nextId = edge.target;
        if (visited.has(nextId)) {
          break;
        }
        visited.add(nextId);

        // Unhide edge and node along the chain
        if (edge.hidden) {
          this.setEdgeHidden(edge.id, false);
        }
        if (this.isNodeHidden(nextId)) {
          this.setNodeHidden(nextId, false);
          changedNodeIds.add(nextId);
        }
        changedSourceIds.add(currentId);
        currentId = nextId;
      }
    },

    // Returns all descendant ids (excluding the start) using BFS
    getDescendantsBFS(startId) {
      const visited = new Set([startId]);
      const queue = [startId];
      const result = [];
      while (queue.length) {
        const current = queue.shift();
        const edges = this.outgoingBySource[current] || [];
        for (const e of edges) {
          const t = e.target;
          if (visited.has(t)) {
            continue;
          }
          visited.add(t);
          result.push(t);
          queue.push(t);
        }
      }
      return result;
    },

    isNodeHidden(nodeId) {
      const idx = this.nodeIndexById[nodeId];
      if (idx === null) {
        return true;
      }
      const n = this.nodes[idx];
      return !!(n.hidden || (n.data && n.data.hidden));
    },

    anyVisibleIncoming(nodeId) {
      const incoming = this.incomingByTarget[nodeId] || [];
      for (const e of incoming) {
        if (!e.hidden) {
          return true;
        }
      }
      return false;
    },

    anyVisibleIncomingFromOutside(nodeId, forbiddenSourcesSet) {
      const incoming = this.incomingByTarget[nodeId] || [];
      for (const e of incoming) {
        if (!e.hidden && !forbiddenSourcesSet.has(e.source)) {
          return true;
        }
      }
      return false;
    },

    setNodeHidden(nodeId, hiddenFlag) {
      const idx = this.nodeIndexById[nodeId];
      if (idx === null) {
        return;
      }
      const node = this.nodes[idx];
      // mutate in place for performance
      node.hidden = hiddenFlag;
      node.data = { ...node.data, hidden: hiddenFlag };
    },

    /**
     * Hide container nodes that have no visible children; show containers that have at least one visible child.
     * Safe to call after visibility changes or when graph is built (does not rely on nodeIndexById).
     */
    updateContainerVisibility() {
      for (const node of this.nodes) {
        const isContainer =
          node.class?.includes('node-container-only') ||
          node.class?.includes('failure-container') ||
          node.class?.includes('success-container');
        if (!isContainer) continue;
        const children = this.nodes.filter((n) => n.parentNode === node.id);
        const hasVisibleChild = children.some(
          (c) => !(c.hidden || (c.data && c.data.hidden)),
        );
        const shouldHide = !hasVisibleChild;
        node.hidden = shouldHide;
        node.data = { ...(node.data || {}), hidden: shouldHide };
      }
    },

    setSourceHighlights(sourceId, handlesArray) {
      const idx = this.nodeIndexById[sourceId];
      if (idx === null) {
        return;
      }
      const node = this.nodes[idx];
      node.data = { ...node.data, highlightedSourceHandles: handlesArray };
    },

    updateHighlightsForSources(sourceIds) {
      for (const sourceId of sourceIds) {
        const edges = this.outgoingBySource[sourceId] || [];
        const highlighted = [];
        for (const e of edges) {
          // Highlight handle if either the target node is hidden or the edge itself is hidden
          if (e.sourceHandle && (this.isNodeHidden(e.target) || e.hidden)) {
            highlighted.push(e.sourceHandle);
          }
        }
        // ensure unique
        this.setSourceHighlights(sourceId, Array.from(new Set(highlighted)));
      }
    },

    recomputeAllHighlights() {
      const allSources = new Set(Object.keys(this.outgoingBySource));
      this.updateHighlightsForSources(allSources);
      this.nodes = [...this.nodes];
    },

    getMyChildren(rootId) {
      return this.edges
        .filter(e => e.source === rootId)
        .map(e => e.target);
    },

    getAllDescendants(rootId) {
      const visited = new Set();
      const descendants = [];

      const visiteNode = (currentId) => {
        if (visited.has(currentId)) {
          return;
        }
        visited.add(currentId);

        const childEdges = this.edges.filter(e => e.source === currentId);

        for (const edge of childEdges) {
          const childNode = this.nodes.find(n => n.id === edge.target);
          if (childNode) {
            descendants.push(childNode.id);
            visiteNode(childNode.id); // recursively visit children
          }
        }
      };

      visiteNode(rootId);

      return descendants;
    },

    addSubGraph(graphData) {
      const prefix = `subgraph_${this.subGraphCounter++}_`;
      const {
        nodes: newNodes = [],
        edges: newEdges = [],
        containers: newContainers = [],
        directAttachmentSourceId = null,
        expandedNodeIds: expandedIdsFromPayload = null,
      } = graphData;
      const expandedIds = expandedIdsFromPayload !== null ? expandedIdsFromPayload : this.expandedNodeIds;
      const existingIds = new Set(this.nodes.map((n) => n.id));

      // 1. Unique ID per node: prefix + original id for all new nodes
      const prefixNodes = newNodes.map((node) => ({
        ...node,
        id: `${prefix}${node.id}`,
      }));

      const prefixEdges = newEdges.map((edge) => ({
        ...edge,
        source: existingIds.has(edge.source) ? edge.source : `${prefix}${edge.source}`,
        target: existingIds.has(edge.target) ? edge.target : `${prefix}${edge.target}`,
      }));

      const prefixContainers = newContainers.map((container) => ({
        id: `${prefix}${container.id}`,
        childIds: container.childIds.map((childId) => `${prefix}${childId}`),
      }));

      // 2. Individual vs container nodes; new vs existing
      const individualNodes = prefixNodes.filter(
        (node) => !prefixContainers.some((c) => c.childIds.includes(node.id)),
      );
      const originalExistingNodeIds = new Set(this.nodes.map((n) => n.id));
      const newIndividualNodes = individualNodes.filter((n) => !originalExistingNodeIds.has(n.id));
      const existingIndividualNodes = individualNodes.filter((n) => originalExistingNodeIds.has(n.id));
      const existingNodeIds = new Set(this.nodes.map((n) => n.id));

      // 3. Temp root nodes when edge goes from existing node to new subgraph (not from clicked node)
      const tempRootNodes = new Map();
      const modifiedEdges = prefixEdges.map((e) => ({ ...e }));
      prefixEdges.forEach((edge) => {
        const fromExisting = originalExistingNodeIds.has(edge.source);
        const toNew = !originalExistingNodeIds.has(edge.target);
        const isFromClicked = !isNil(directAttachmentSourceId) && edge.source === directAttachmentSourceId;
        if (fromExisting && toNew && !isFromClicked) tempRootNodes.set(edge.source, null);
      });
      tempRootNodes.forEach((_, sourceId) => {
        const sourceNode = this.nodes.find((n) => n.id === sourceId);
        if (!sourceNode) return;
        const tempRootId = `${prefix}temp_${sourceId}`;
        tempRootNodes.set(sourceId, {
          id: tempRootId,
          originalId: sourceId,
          data: { ...(sourceNode.data || {}), label: sourceNode.data?.title ?? sourceNode.data?.label ?? sourceId },
          width: NODE_WIDTH,
        });
        modifiedEdges.forEach((edge) => {
          if (edge.source === sourceId && !originalExistingNodeIds.has(edge.target)) edge.source = tempRootId;
        });
      });

      // Add temp root nodes to the nodes list for layout
      const tempRootNodesList = Array.from(tempRootNodes.values());
      const allNodesForLayout = [...newIndividualNodes, ...tempRootNodesList];

      // Step 2: Find root node: prefer temp roots, then node with no incoming edges, then first node
      const rootNode = tempRootNodesList[0]
        || allNodesForLayout.find((node) => !modifiedEdges.some((edge) => edge.target === node.id))
        || allNodesForLayout[0]
        || prefixNodes[0];

      if (!rootNode) return;

      // Step 3: Layout with Dagre
      const individualEdges = modifiedEdges.filter((edge) => {
        return allNodesForLayout.some((n) => n.id === edge.source)
            && allNodesForLayout.some((n) => n.id === edge.target);
      });

      const individualPositions =
        allNodesForLayout.length > 0 ? this.layoutNodesWithDagre(allNodesForLayout, individualEdges) : new Map();

      existingIndividualNodes.forEach((node) => {
        const existing = this.nodes.find((n) => n.id === node.id);
        if (existing) {
          individualPositions.set(node.id, { x: existing.position.x, y: existing.position.y });
        }
      });

      // Step 4: Layout failure container children (only new children need layout)
      const failureContainer = prefixContainers.find((c) => c.id.includes('failure'));
      const failureLayout = failureContainer
        ? this.layoutContainerChildren(failureContainer, prefixNodes, prefixEdges)
        : null;

      // Step 5: Layout success container children (only new children need layout)
      const successContainer = prefixContainers.find((c) => c.id.includes('success'));
      const successLayout = successContainer
        ? this.layoutContainerChildren(successContainer, prefixNodes, prefixEdges)
        : null;

      // Step 6: Calculate offset to place sub graph in free space (under clicked node, alternate left/right)
      // Pass failure/success layout so we reserve their space and avoid overlapping existing containers
      const offset = this.calculateSubGraphOffset(individualPositions, directAttachmentSourceId, failureLayout, successLayout);

      // Step 7: Position failure container (left, first child level)
      const failureContainerPos = failureLayout
        ? this.positionFailureContainer(individualPositions, rootNode, failureLayout, offset)
        : null;

      // Step 8: Position success container (right, first child level)
      const successContainerPos = successLayout
        ? this.positionSuccessContainer(individualPositions, rootNode, successLayout, offset, failureContainerPos, failureLayout)
        : null;

      // Step 9: Build and add nodes to canvas
      const flowNodes = [...this.nodes];
      const flowEdges = [...this.edges];

      // Add temp root nodes first
      tempRootNodesList.forEach((tempRoot) => {
        const pos = individualPositions.get(tempRoot.id);
        if (pos) {
          const sourceNode = this.nodes.find((n) => n.id === tempRoot.originalId);
          flowNodes.push({
            id: tempRoot.id,
            type: sourceNode?.type || 'default',
            position: {
              x: pos.x + offset.x,
              y: pos.y + offset.y,
            },
            width: NODE_WIDTH,
            data: { ...(tempRoot.data || {}), label: tempRoot.data?.title ?? tempRoot.data?.label ?? tempRoot.id },
            draggable: true,
            style: {
              border: '2px dashed #6366f1', // Dotted/dashed border for temp root
              borderRadius: '8px',
            },
          });
          existingNodeIds.add(tempRoot.id);
        }
      });

      // Add individual nodes (unique id = prefix + original)
      newIndividualNodes.forEach((node) => {
        if (existingNodeIds.has(node.id)) return;
        const pos = individualPositions.get(node.id);
        if (!pos) return;
        const originalId = node.id.slice(prefix.length);
        const flowNode = {
          id: node.id,
          type: node.type || 'default',
          position: { x: pos.x + offset.x, y: pos.y + offset.y },
          width: NODE_WIDTH,
          data: {
            ...(node.data || {}),
            label: node.data?.title ?? node.data?.label ?? originalId,
            originalId,
            hideExpand: node.data?.hideExpand === true || expandedIds.includes(node.id) || expandedIds.includes(originalId) || !IQ_TOOL_DIAGRAM_EXPANDABLE_NODE_TYPES.includes(node.type),
          },
          draggable: true,
        };
        if (node.class) flowNode.class = node.class;
        if (node.style) flowNode.style = node.style;
        flowNodes.push(flowNode);
        existingNodeIds.add(node.id);
      });

      // Add failure container and children
      if (failureContainer && failureContainerPos && failureLayout) {
        flowNodes.push({
          id: failureContainer.id,
          type: 'default',
          class: 'node-container-only failure-container',
          position: failureContainerPos,
          width: failureLayout.size.width,
          height: failureLayout.size.height,
          style: {
            width: `${failureLayout.size.width}px`,
            height: `${failureLayout.size.height}px`,
            borderRadius: '8px',
          },
          // Don't show container title
          data: {},
          selectable: true,
          draggable: true,
          connectable: false,
        });

        failureContainer.childIds.forEach((childId) => {
          if (existingNodeIds.has(childId)) return;
          const childPos = failureLayout.positions.get(childId);
          if (!childPos) return;
          const originalId = childId.slice(prefix.length);
          const childDef = prefixNodes.find((n) => n.id === childId);
          flowNodes.push({
            id: childId,
            type: childDef?.type || 'default',
            position: { x: childPos.x, y: childPos.y },
            width: NODE_WIDTH,
            data: { ...(childDef?.data || {}), label: childDef?.data?.title ?? childDef?.data?.label ?? originalId, originalId },
            parentNode: failureContainer.id,
            extent: 'parent',
            draggable: false,
          });
          existingNodeIds.add(childId);
        });
      }

      // Add success container and children
      if (successContainer && successContainerPos && successLayout) {
        flowNodes.push({
          id: successContainer.id,
          type: 'default',
          class: 'node-container-only success-container',
          position: successContainerPos,
          width: successLayout.size.width,
          height: successLayout.size.height,
          style: {
            width: `${successLayout.size.width}px`,
            height: `${successLayout.size.height}px`,
            borderRadius: '8px',
          },
          // Don't show container title
          data: {},
          selectable: true,
          draggable: true,
          connectable: false,
        });

        successContainer.childIds.forEach((childId) => {
          if (existingNodeIds.has(childId)) return;
          const childPos = successLayout.positions.get(childId);
          if (!childPos) return;
          const originalId = childId.slice(prefix.length);
          const childDef = prefixNodes.find((n) => n.id === childId);
          flowNodes.push({
            id: childId,
            type: childDef?.type || 'default',
            position: { x: childPos.x, y: childPos.y },
            width: NODE_WIDTH,
            data: { ...(childDef?.data || {}), label: childDef?.data?.title ?? childDef?.data?.label ?? originalId, originalId },
            parentNode: successContainer.id,
            extent: 'parent',
            draggable: false,
          });
          existingNodeIds.add(childId);
        });
      }

      // Add edges connecting original source to temp root nodes
      tempRootNodes.forEach((tempRoot, originalSourceId) => {
        const edgeId = `edge-${originalSourceId}-${tempRoot.id}`;
        const sourceExists = flowNodes.some((n) => n.id === originalSourceId);
        const targetExists = flowNodes.some((n) => n.id === tempRoot.id);

        if (sourceExists && targetExists) {
          // Source node type is already set, no need to change it
          flowEdges.push({
            id: edgeId,
            source: originalSourceId,
            target: tempRoot.id,
            type: 'default',
            animated: false,
            zIndex: -1,
            sourceHandle: 'source-bottom',
            targetHandle: 'target-top',
            sourcePosition: 'bottom',
            targetPosition: 'top',
            style: {
              stroke: '#9ca3af',
              strokeWidth: 1,
              strokeDasharray: '5,5',
            },
          });
        }
      });

      // Add edges, skipping duplicates and validating both endpoints exist
      const addedEdgeIds = new Set(this.edges.map((e) => e.id));
      tempRootNodes.forEach((tempRoot, originalSourceId) => {
        addedEdgeIds.add(`edge-${originalSourceId}-${tempRoot.id}`);
      });

      modifiedEdges.forEach((edge) => {
        const edgeId = `edge-${edge.source}-${edge.target}`;
        if (addedEdgeIds.has(edgeId)) return;
        const sourceExists = flowNodes.some((n) => n.id === edge.source);
        const targetExists = flowNodes.some((n) => n.id === edge.target);

        if (!sourceExists || !targetExists) {
          return;
        }

        const sourceNode = flowNodes.find((n) => n.id === edge.source);
        const targetNode = flowNodes.find((n) => n.id === edge.target);

        // Skip edges connecting to/from container nodes
        const sourceIsContainer = sourceNode?.class?.includes('container') || sourceNode?.class?.includes('-container');
        const targetIsContainer = targetNode?.class?.includes('container') || targetNode?.class?.includes('-container');
        if (sourceIsContainer || targetIsContainer) {
          return;
        }

        if (sourceNode?.parentNode && targetNode?.parentNode && sourceNode.parentNode === targetNode.parentNode) {
          const hasExplicitEdge = newEdges.some((e) => {
            const src = existingIds.has(e.source) ? e.source : `${prefix}${e.source}`;
            const tgt = existingIds.has(e.target) ? e.target : `${prefix}${e.target}`;
            return src === edge.source && tgt === edge.target;
          });
          if (!hasExplicitEdge) return;
        }

        const edgeConfig = {
          id: edgeId,
          source: edge.source,
          target: edge.target,
          type: edge.type || 'default',
          animated: false,
        };

        if (edge.style) {
          edgeConfig.style = { ...edge.style, strokeWidth: edge.style.strokeWidth ?? 1 };
        }

        const isDirectAttachmentEdge = directAttachmentSourceId !== null && edge.source === directAttachmentSourceId;
        const EDGE_Z_INDEX = 2; // eslint-disable-line no-magic-numbers -- above container z-index (1)
        edgeConfig.zIndex = isDirectAttachmentEdge ? -1 : EDGE_Z_INDEX;
        edgeConfig.sourceHandle = 'source-bottom';
        edgeConfig.targetHandle = 'target-top';

        if (edge.label) edgeConfig.label = edge.label;

        flowEdges.push(edgeConfig);
        addedEdgeIds.add(edgeId);
      });

      // Hide expand button on already-expanded nodes
      flowNodes.forEach((n) => {
        if (expandedIds.includes(n.id) || (n.data?.originalId && expandedIds.includes(n.data.originalId))) {
          n.data = { ...(n.data || {}), hideExpand: true };
        }
      });

      this.nodes = flowNodes;
      this.edges = flowEdges;
      this.updateContainerVisibility();
    },

    /**
     * Layout nodes using Dagre algorithm (independent positioning)
     */
    layoutNodesWithDagre(nodes, edges) {
      if (nodes.length === 0) return new Map();

      const g = new dagre.graphlib.Graph();
      g.setDefaultEdgeLabel(() => ({}));
      g.setGraph({
        rankdir: 'TB',
        nodesep: this.NODE_GAP,
        ranksep: this.RANK_GAP,
      });

      // Add nodes with fixed width and default height for layout
      nodes.forEach((node) => {
        g.setNode(node.id, {
          width: NODE_WIDTH,
          height: DEFAULT_NODE_HEIGHT_FOR_LAYOUT,
        });
      });

      // Add edges
      edges.forEach((edge) => {
        g.setEdge(edge.source, edge.target);
      });

      // Run layout
      dagre.layout(g);

      // Extract positions (Dagre gives center, convert to top-left)
      const positions = new Map();
      const HALF = 2;
      g.nodes().forEach((nodeId) => {
        const node = g.node(nodeId);
        if (node.x !== undefined && node.y !== undefined) {
          positions.set(nodeId, {
            x: node.x - NODE_WIDTH / HALF,
            y: node.y - DEFAULT_NODE_HEIGHT_FOR_LAYOUT / HALF,
          });
        }
      });

      return positions;
    },

    /**
     * Layout container children using Dagre
     */
    layoutContainerChildren(container, allNodes, allEdges) {
      // Use all child nodes for edge filtering (to get all relevant edges)
      const childNodes = allNodes.filter((n) => container.childIds.includes(n.id));
      const childEdges = allEdges.filter(
        (e) => container.childIds.includes(e.source) && container.childIds.includes(e.target),
      );

      if (childNodes.length === 0) {
        return {
          positions: new Map(),
          size: { width: NODE_WIDTH, height: DEFAULT_NODE_HEIGHT_FOR_LAYOUT },
        };
      }

      const g = new dagre.graphlib.Graph();
      g.setDefaultEdgeLabel(() => ({}));
      g.setGraph({
        rankdir: 'TB',
        nodesep: this.CONTAINER_CHILD_GAP,
        ranksep: this.CONTAINER_CHILD_GAP,
      });

      // Add nodes with fixed width and default height for layout
      childNodes.forEach((node) => {
        g.setNode(node.id, {
          width: NODE_WIDTH,
          height: DEFAULT_NODE_HEIGHT_FOR_LAYOUT,
        });
      });

      // Add edges
      childEdges.forEach((edge) => {
        g.setEdge(edge.source, edge.target);
      });

      // Run layout
      dagre.layout(g);

      // Extract positions and calculate bounds
      const positions = new Map();
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      const HALF = 2;
      g.nodes().forEach((nodeId) => {
        const node = g.node(nodeId);
        if (node.x !== undefined && node.y !== undefined) {
          const x = node.x - NODE_WIDTH / HALF;
          const y = node.y - DEFAULT_NODE_HEIGHT_FOR_LAYOUT / HALF;

          positions.set(nodeId, { x, y });

          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x + NODE_WIDTH);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y + DEFAULT_NODE_HEIGHT_FOR_LAYOUT);
        }
      });

      // Calculate container size with padding
      const PADDING_MULTIPLIER = 2;
      const width = maxX - minX + this.CONTAINER_PADDING * PADDING_MULTIPLIER;
      const height = maxY - minY + this.CONTAINER_PADDING * PADDING_MULTIPLIER;

      // Adjust positions relative to container top-left (with padding)
      const adjustedPositions = new Map();
      positions.forEach((pos, nodeId) => {
        adjustedPositions.set(nodeId, {
          x: pos.x - minX + this.CONTAINER_PADDING,
          y: pos.y - minY + this.CONTAINER_PADDING,
        });
      });

      return {
        positions: adjustedPositions,
        size: { width, height },
      };
    },

    /**
     * Get position { x, y } for a node (handles Vue Flow / reactive shape).
     */
    getNodePosition(node) {
      const p = node.position;
      // Vue Flow can leave position undefined before layout; treat null and undefined as "no position"
      if (isNil(p)) return { x: 0, y: 0 };
      const x = typeof p.x === 'number' ? p.x : Number(p.x);
      const y = typeof p.y === 'number' ? p.y : Number(p.y);
      return { x: Number.isFinite(x) ? x : 0, y: Number.isFinite(y) ? y : 0 };
    },

    /**
     * Get width/height for a node, including failure/success containers (use style or explicit dimensions).
     */
    getNodeDimensions(node) {
      const isContainer = node.class?.includes('container') || node.class?.includes('-container');
      const isFailureOrSuccess = node.id?.includes('failure_container') || node.id?.includes('success_container') || node.class?.includes('failure-container') || node.class?.includes('success-container');
      let w = node.width;
      let h = node.height;
      if (node.style) {
        // Treat null and undefined as missing so we can fall back to style (Vue Flow can leave width/height unset)
        if (isNil(w) && !isNil(node.style.width)) {
          w = typeof node.style.width === 'number' ? node.style.width : parseInt(String(node.style.width), 10);
        }
        if (isNil(h) && !isNil(node.style.height)) {
          h = typeof node.style.height === 'number' ? node.style.height : parseInt(String(node.style.height), 10);
        }
      }
      const widthMultiplier = 3; // eslint-disable-line no-magic-numbers
      const defaultW = isContainer || isFailureOrSuccess ? NODE_WIDTH * widthMultiplier : NODE_WIDTH;
      const defaultH = isContainer || isFailureOrSuccess ? DEFAULT_NODE_HEIGHT_FOR_LAYOUT * widthMultiplier : DEFAULT_NODE_HEIGHT_FOR_LAYOUT;
      if (isNil(w) || Number.isNaN(w) || w <= 0) w = defaultW;
      if (isNil(h) || Number.isNaN(h) || h <= 0) h = defaultH;
      return { width: w, height: h };
    },

    /**
     * Get bounding boxes of all top-level existing nodes for collision detection.
     * Explicitly includes failure and success containers with their full area so subgraphs don't overlap them.
     * @returns {Array<{left: number, top: number, right: number, bottom: number}>}
     */
    getExistingNodeBounds() {
      const bounds = [];
      this.nodes.forEach((node) => {
        if (node.parentNode) return;
        const pos = this.getNodePosition(node);
        const { width: w, height: h } = this.getNodeDimensions(node);
        bounds.push({
          left: pos.x,
          top: pos.y,
          right: pos.x + w,
          bottom: pos.y + h,
        });
      });
      return bounds;
    },

    /**
     * Check if two axis-aligned boxes overlap (with optional gap).
     */
    boxesOverlap(a, b, gap = 0) {
      return a.left - gap < b.right && a.right + gap > b.left && a.top - gap < b.bottom && a.bottom + gap > b.top;
    },

    /**
     * Calculate offset to place sub graph in free space.
     * - Vertical: place below the lowest point of the existing diagram (block borders).
     * - Horizontal: when directAttachmentSourceId is set, alternate left/right of clicked node; then shift to avoid overlap.
     * - Bounds include reserved space for this subgraph's failure/success containers so they don't overlap existing ones.
     */
    calculateSubGraphOffset(individualPositions, directAttachmentSourceId = null, failureLayout = null, successLayout = null) {
      if (!individualPositions || individualPositions.size === 0) {
        const existingMaxY = this.getMaxY();
        return { x: 0, y: existingMaxY > 0 ? existingMaxY + this.RANK_GAP : 0 };
      }

      // Find bounds of new sub graph (before offset)
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      individualPositions.forEach((pos) => {
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x);
        maxY = Math.max(maxY, pos.y);
      });

      const subgraphWidth = (maxX - minX) + NODE_WIDTH;
      const subgraphHeight = (maxY - minY) + DEFAULT_NODE_HEIGHT_FOR_LAYOUT;
      const gap = this.NODE_GAP;
      // Reserve space for failure (left) and success (right) containers so overlap check uses full block
      const failureReserve = failureLayout ? failureLayout.size.width + gap : 0;
      const successReserve = successLayout ? successLayout.size.width + gap : 0;

      // Level-based placement: find a row that contains the clicked node (same level), or create a new row (next level)
      const blockBottomY = this.getMaxY();
      let useRowY = blockBottomY > 0 ? blockBottomY + this.RANK_GAP : 0;
      if (directAttachmentSourceId) {
        const rowForClicked = this.subgraphRows.find((row) => row.nodeIds.has(directAttachmentSourceId));
        if (rowForClicked) {
          useRowY = rowForClicked.y;
        } else {
          useRowY = blockBottomY > 0 ? blockBottomY + this.RANK_GAP : 0;
          this.subgraphRows.push({
            y: useRowY,
            nodeIds: new Set(this.nodes.map((n) => n.id)),
          });
        }
      }
      const offsetY = useRowY - minY;

      const existingBounds = this.getExistingNodeBounds();

      // Bounds include reserved space for failure/success containers so we don't place over existing containers
      const getSubgraphBounds = (ox) => ({
        left: ox + minX - failureReserve,
        top: offsetY + minY,
        right: ox + minX + subgraphWidth + successReserve,
        bottom: offsetY + minY + subgraphHeight,
      });

      const findOverlapping = (ox) => {
        const box = getSubgraphBounds(ox);
        return existingBounds.find((existing) => this.boxesOverlap(box, existing, gap));
      };

      const overlapsAny = (ox) => !isNil(findOverlapping(ox));

      // Expansion index: 0 = prefer left of clicked node, 1 = prefer right (alternate for each new subgraph)
      const expansionIndex = (this.subGraphCounter - 1) % 2; // eslint-disable-line no-magic-numbers
      let offsetX;

      if (directAttachmentSourceId) {
        const clickedNode = this.nodes.find((n) => n.id === directAttachmentSourceId);
        if (clickedNode) {
          const nodeWidth = clickedNode.width || (clickedNode.style?.width ? parseInt(clickedNode.style.width, 10) : null) || NODE_WIDTH;
          if (expansionIndex === 0) {
            offsetX = clickedNode.position.x - gap - (maxX + NODE_WIDTH);
          } else {
            offsetX = clickedNode.position.x + nodeWidth + gap - minX;
          }
        } else {
          const existingMaxX = this.getMaxX();
          offsetX = existingMaxX > 0 ? existingMaxX + gap - minX : -minX;
        }
      } else {
        const existingMaxX = this.getMaxX();
        offsetX = existingMaxX > 0 ? existingMaxX + gap - minX : -minX;
      }

      // Shift horizontally to avoid overlapping: shift by exact overlap amount first, then by full steps
      const shiftStep = subgraphWidth + gap;
      const maxIterations = 80;
      let iterations = 0;
      while (overlapsAny(offsetX) && iterations < maxIterations) {
        const overlap = findOverlapping(offsetX);
        if (overlap) {
          const box = getSubgraphBounds(offsetX);
          if (expansionIndex === 0) {
            const shiftNeeded = box.right - (overlap.left - gap);
            offsetX -= shiftNeeded > 0 ? shiftNeeded : shiftStep;
          } else {
            const shiftNeeded = overlap.right + gap - box.left;
            offsetX += shiftNeeded > 0 ? shiftNeeded : shiftStep;
          }
        } else {
          break;
        }
        iterations += 1;
      }

      return { x: offsetX, y: offsetY };
    },

    /**
     * Position failure container to the left, at first child level
     */
    positionFailureContainer(individualPositions, rootNode, failureLayout, offset) {
      const rootPos = individualPositions.get(rootNode.id);
      if (!rootPos) return null;

      // Find level 1 nodes (direct children of root)
      // Use default height for layout calculation
      const level1Y = rootPos.y + DEFAULT_NODE_HEIGHT_FOR_LAYOUT + this.RANK_GAP;

      // Position to the left of the graph
      const graphLeftX = this.getGraphLeftX(individualPositions);
      const failureX = graphLeftX - failureLayout.size.width - this.NODE_GAP;

      return {
        x: failureX + offset.x,
        y: level1Y + offset.y,
      };
    },

    /**
     * Position success container to the right, at first child level
     */
    positionSuccessContainer(individualPositions, rootNode, successLayout, offset, failureContainerPos, failureLayout) {
      const rootPos = individualPositions.get(rootNode.id);
      if (!rootPos) return null;

      // Find level 1 Y position (should be same for all level 1 nodes)
      // Use default height for layout calculation
      const level1Y = rootPos.y + DEFAULT_NODE_HEIGHT_FOR_LAYOUT + this.RANK_GAP;

      // Calculate rightmost X of ALL individual nodes (AFTER offset is applied)
      // This ensures success container is to the right of the entire graph
      let graphRightX = rootPos.x + offset.x + NODE_WIDTH;

      individualPositions.forEach((pos) => {
        const absoluteX = pos.x + offset.x;
        graphRightX = Math.max(graphRightX, absoluteX + NODE_WIDTH);
      });

      // Position success container to the right of the graph's individual nodes
      let successX = graphRightX + this.NODE_GAP;

      // Check for collision with failure container (if in same graph)
      if (failureContainerPos && failureLayout) {
        const failureRightX = failureContainerPos.x + failureLayout.size.width;
        // Only adjust if failure is actually to the right of our calculated position
        // (This shouldn't happen normally since failure is on the left, but just in case)
        if (failureRightX > successX) {
          successX = failureRightX + this.NODE_GAP;
        }
      }

      return {
        x: successX,
        y: level1Y + offset.y,
      };
    },

    /**
     * Get leftmost X position of graph
     */
    getGraphLeftX(individualPositions) {
      let minX = Infinity;
      individualPositions.forEach((pos) => {
        minX = Math.min(minX, pos.x);
      });
      return minX === Infinity ? 0 : minX;
    },

    /**
     * Get maximum Y position of existing nodes (including containers).
     */
    getMaxY() {
      if (this.nodes.length === 0) return 0;

      let maxY = 0;
      this.nodes.forEach((node) => {
        if (!node.parentNode) {
          const pos = this.getNodePosition(node);
          const { height } = this.getNodeDimensions(node);
          maxY = Math.max(maxY, pos.y + height);
        }
      });
      return maxY;
    },

    /**
     * Get maximum X position of existing nodes (including containers).
     */
    getMaxX() {
      if (this.nodes.length === 0) return 0;

      let maxX = -Infinity;
      this.nodes.forEach((node) => {
        if (!node.parentNode) {
          const pos = this.getNodePosition(node);
          const { width } = this.getNodeDimensions(node);
          maxX = Math.max(maxX, pos.x + width);
        }
      });
      return maxX === -Infinity ? 0 : maxX;
    },

    async layoutGraph() {
      await nextTick();
      await new Promise(r => requestAnimationFrame(r));

      if (this.nodes?.length > 0) {
        await nextTick();

        // Only fit view on initial load; keep current viewport when expanding nodes
        if (this.subGraphCounter <= 1) {
          this.$refs.vueFlow.fitView();
          const viewport = this.$refs.vueFlow.getViewport();
          if (viewport.zoom > DIAGRAM_ZOOM_DEFAULT) {
            this.$refs.vueFlow.viewportHelper.zoomTo(DIAGRAM_ZOOM_DEFAULT);
          }
        }

        this.resetDiagram();
      }
    },

    handleNodeClick({ node }) {
      if (this.isLocked) {
        return;
      }

      const selectedNode = node;
      const childrenEdges = this.outgoingBySource[selectedNode.id] || [];
      const childIds = childrenEdges.map(e => e.target);

      // Determine action based on edge or node visibility:
      // if any immediate child edge is hidden, or any child node is hidden => expand; else collapse
      const anyChildEdgeHidden = childrenEdges.some(e => !!e.hidden);
      const anyChildNodeHidden = childIds.some(id => this.isNodeHidden(id));
      const shouldExpand = anyChildEdgeHidden || anyChildNodeHidden;

      if (shouldExpand) {
        // Expand: show immediate children only
        const changed = new Set();
        for (const cid of childIds) {
          // ensure child visible
          if (this.isNodeHidden(cid)) {
            this.setNodeHidden(cid, false);
            changed.add(cid);
          }
          // show edge from selected -> child
          const edge = this.findEdgeFromTo(selectedNode.id, cid);
          if (edge) {
            this.setEdgeHidden(edge.id, false);
          }
          // expand full linear chain starting from this child
          let currentId = cid;
          const visitedChain = new Set([selectedNode.id, cid]);
          for (;;) {
            const nextEdges = this.outgoingBySource[currentId] || [];
            if (nextEdges.length !== 1) {
              break;
            }
            const nextId = nextEdges[0].target;
            if (visitedChain.has(nextId)) {
              break;
            }
            visitedChain.add(nextId);
            if (this.isNodeHidden(nextId)) {
              this.setNodeHidden(nextId, false);
              changed.add(nextId);
            }
            // show edge current -> next
            const e2 = this.findEdgeFromTo(currentId, nextId);
            if (e2) {
              this.setEdgeHidden(e2.id, false);
            }
            currentId = nextId;
          }
        }
        // Update highlights for the selected node and any sources pointing to changed nodes
        const sourcesToUpdate = new Set([selectedNode.id]);
        for (const nid of changed) {
          const incoming = this.incomingByTarget[nid] || [];
          for (const e of incoming) {
            sourcesToUpdate.add(e.source);
          }
        }
        this.updateHighlightsForSources(sourcesToUpdate);
      } else {
        // Collapse: hide all edges from the collapsed subtree; hide nodes that have no visible incoming edges left
        const descendants = this.getDescendantsBFS(selectedNode.id);
        const collapsedSources = new Set([selectedNode.id, ...descendants]);

        // Fast path: if all immediate children remain visible via another parent, only hide edges from selected -> child
        const childrenEdges = this.outgoingBySource[selectedNode.id] || [];
        const childIds = childrenEdges.map(e => e.target);
        const allChildrenKeptVisible = childIds.length > 0 && childIds.every(cid => this.anyVisibleIncomingFromOutside(cid, collapsedSources));
        if (allChildrenKeptVisible) {
          for (const cid of childIds) {
            const e = this.findEdgeFromTo(selectedNode.id, cid);
            if (e) {
              this.setEdgeHidden(e.id, true);
            }
          }
          // Only the selected node's handles need an update in this case
          this.updateHighlightsForSources(new Set([selectedNode.id]));
          this.nodes = [...this.nodes];
          this.edges = [...this.edges];
          return;
        }

        // Hide edges from subtree sources, except keep edges from sources that are supported by another visible parent outside the subtree
        for (const sourceId of collapsedSources) {
          const outs = this.outgoingBySource[sourceId] || [];
          // Always hide edges from the clicked node itself
          const shouldKeepEdges = sourceId !== selectedNode.id && this.anyVisibleIncomingFromOutside(sourceId, collapsedSources);
          for (const e of outs) {
            if (shouldKeepEdges) {
              continue;
            }
            this.setEdgeHidden(e.id, true);
          }
        }

        // Hide nodes that no longer have any visible incoming edges
        const changed = new Set();
        const hiddenNow = new Set();
        for (const did of descendants) {
          const shouldHide = !this.anyVisibleIncoming(did);
          if (shouldHide && !this.isNodeHidden(did)) {
            this.setNodeHidden(did, true);
            changed.add(did);
            hiddenNow.add(did);
          }
          if (!shouldHide && this.isNodeHidden(did)) {
            // ensure visible if they still have a visible parent
            this.setNodeHidden(did, false);
            changed.add(did);
          }
        }

        // Hide all incoming edges to nodes that were just hidden
        for (const did of hiddenNow) {
          const incoming = this.incomingByTarget[did] || [];
          for (const e of incoming) {
            this.setEdgeHidden(e.id, true);
          }
        }

        // Ensure linear chains from kept-visible nodes are fully opened
        const keptVisible = descendants.filter(d => this.anyVisibleIncoming(d));
        const chainChangedNodes = new Set();
        const chainSources = new Set();
        for (const kv of keptVisible) {
          this.expandLinearChainFrom(kv, chainChangedNodes, chainSources);
        }

        // Update highlights for selected node and any sources that point to changed nodes
        const sourcesToUpdate = new Set([selectedNode.id, ...chainSources]);
        for (const nid of changed) {
          const incoming = this.incomingByTarget[nid] || [];
          for (const e of incoming) {
            sourcesToUpdate.add(e.source);
          }
        }
        for (const nid of chainChangedNodes) {
          const incoming = this.incomingByTarget[nid] || [];
          for (const e of incoming) {
            sourcesToUpdate.add(e.source);
          }
        }
        this.updateHighlightsForSources(sourcesToUpdate);
      }

      this.updateContainerVisibility();
      // trigger update to propagate changes to VueFlow
      this.nodes = [...this.nodes];
      this.edges = [...this.edges];
    },
  },
};
</script>

<style lang="scss">
@use './Diagram.scss' as *;
</style>

<style scoped>
.iq-tools-view-map-container {
  width: 100%;
  height: 100%;
}

.iq-tools-view-map-inner {
  background-color: #efefef;
  height: 100%;
  width: 100%;
}

/* Custom styling for bigger controls */
:deep(.vue-flow__controls) {
  transform: scale(1.2);
  transform-origin: top right;
}

:deep(.vue-flow__controls-button) {
  width: 20px !important;
  height: 20px !important;
  font-size: 18px !important;
  border-radius: 6px !important;
  margin: 4px !important;
}

:deep(.vue-flow__controls-button svg) {
  width: 20px !important;
  height: 20px !important;
}

:deep(.vue-flow__controls) {
  background: #777;
}

/* Style for temp root nodes (dotted border) */
:deep(.vue-flow__node[style*="dashed"]) {
  border-style: dashed !important;
}

.failure-container {
  border: 1px solid #f84b4b !important;
  background-color: #f7d9d9 !important;
  box-shadow: none !important;
  opacity: .5 !important;
}

.success-container {
  border: 1px solid #06ac50 !important;
  background-color: #b5f5ce !important;
  box-shadow: none !important;
  opacity: .5 !important;
}

/* Hide container label text ("failure"/"success") */
:deep(.vue-flow__node.failure-container .vue-flow__node-label),
:deep(.vue-flow__node.success-container .vue-flow__node-label) {
  display: none !important;
}
</style>